import React, { Fragment } from 'react';
import { compose, equals, omit, prop } from 'ramda';
import ResizeObserver from 'resize-observer-polyfill';
import { uniqueId } from '@core/js/utils/methods.util';
import Slider from '@core/js/components/common/Slider';
import { useUpdateEffect } from '@core/js/hooks/useUpdateEffect';
import { usePortletConfig } from '@core/js/components/system/Portlet';
import { Game as CommonGame, EmptyGame } from '@core/js/components/common/Game';
import { defaultProps, mapProps, setDisplayName, shouldUpdate, withHandlers, withState } from 'recompose';
import { useDeviceSelector } from '@core/js/store/hooks';

import withGamesSlides from './withGamesSlides';
import { DISPLAY_MODE, GAME_SIZES } from './constants';

import style from './index.scss';

export const SLIDER_OPTIONS = {
    animation: {
        type: 'monoSlider'
    },
    controls: {
        arrows: { enabled: true },
        bullets: {
            enabled: false,
            position: {
                x: 'right',
                y: 'top',
                offsetX: '4em',
                offsetY: '0em'
            }
        },
        buttons: { enabled: false }
    }
};

export const GridPagingSlider = ({
    className = '',
    classNames = {},
    slides,
    layout,
    gamesPerSlide,
    displayMode,
    gameSize,
    setSlideRef,
    setGameRef,
    collapseAnimation,
    expandAnimation,
    slideHeight,
    sliderOptions,
    controlsEnabled = false,
    handlerTransitionEnd,
    Game,
    showGameInfo,
    swipe = false,
    isBulletsClickable,
    showFeatureIcons = true,
    onActiveSlideChange,
    showControlsOnHover = true,
    expanded,
    categoryModificators,
    isJackpotCategory,
    isBannerCategory,
    source,
    categoryName,
    feedName
}) => {
    const sliderCls = `${className} ${gameSize === GAME_SIZES.EXTRA_SMALL ? style.extraSmall : ''}`;
    const { showViewMore } = usePortletConfig();
    const innerSliderCls =
        `${style.innerSlider}
         ${classNames.slider || ''}
         ${displayMode === DISPLAY_MODE.GRID ? style.grid : ''}
         ${collapseAnimation ? `${style.grid} ${style.collapseAnimation}` : ''}
         ${expandAnimation ? style.expandAnimation : ''}`;

    const gameCls = `${style.game} ${classNames.game || ''}`;
    const slideCls = `${style.slide} ${classNames.slide || ''}`;

    const isDesktop = useDeviceSelector(prop('isDesktop'));

    const innerSliderStyles = slideHeight != null
        ? {
            height: displayMode !== DISPLAY_MODE.GRID
                ? `${slideHeight}px`
                : collapseAnimation
                    ? `${slideHeight}px`
                    : 'auto'
        }
        : {};

    return (
        <Slider
            options={sliderOptions}
            isBulletsClickable={isBulletsClickable}
            rotation={false}
            expanded={expanded}
            swipe={swipe}
            showControls={displayMode === DISPLAY_MODE.PAGING}
            renderAll={displayMode === DISPLAY_MODE.GRID || !isDesktop}
            showControlsOnHover={showControlsOnHover}
            className={sliderCls}
            classNames={{
                slide: slideCls,
                sliderContainer: style.sliderContainer,
                slider: innerSliderCls,
                arrows: style.arrows,
                bullet: style.bullet,
                bullets: style.bullets,
                activeBullet: style.activeBullet
            }}
            controlsEnabled={controlsEnabled}
            layout={layout}
            slideLength={gamesPerSlide}
            styles={{ slider: innerSliderStyles }}
            handlerSliderTransitionEnd={handlerTransitionEnd}
            refs={{ slide: setSlideRef }}
            isAllItemsVisible={expanded || !showViewMore}
            onActiveSlideChange={onActiveSlideChange}>
            {slides.map((slide, i) => (
                <Fragment key={`${slide.length}_${i}_${slides[0][0]}`}>
                    {slide.map((tile, index) => {
                        const code = layout ? tile.code : tile;
                        const size = tile?.size || { width: 1, height: 1 };
                        const ratingItem = index + i * gamesPerSlide;

                        if (!code) {
                            return <EmptyGame key={uniqueId(index)} className={gameCls} />;
                        }

                        return (
                            <Game key={code}
                                {...(i === 0 && index === 0 && { setGameRef })}
                                code={code}
                                sizes={size}
                                className={gameCls}
                                showGameInfo={showGameInfo}
                                showFeatureIcons={showFeatureIcons}
                                showJackpotTicker={!isJackpotCategory}
                                source={source}
                                feedName={feedName}
                                categoryName={categoryName}
                                ratingItem={ratingItem + 1}
                                categoryModificators={categoryModificators}
                                isBannerCategory={isBannerCategory}
                            />
                        );
                    })}
                </Fragment>
            ))}
        </Slider>
    );
};

export default compose(
    setDisplayName('GridPagingSlider'),
    defaultProps({
        isVisible: true,
        Game: CommonGame,
        sliderOptions: SLIDER_OPTIONS
    }),
    shouldUpdate((props, nextProps) => {
        const { gameCodes, expanded, isVisible } = nextProps;

        return isVisible && (
            isVisible !== props.isVisible
            || expanded !== props.expanded
            || !equals(gameCodes, props.gameCodes)
        );
    }),
    withGamesSlides,
    withState('expandAnimation', 'setExpandAnimation', false),
    withState('collapseAnimation', 'setCollapseAnimation', false),
    withState('slideHeight', 'setSlideHeight', null),
    withState('resizeObserver', 'setResizeObserver',
        ({ setSlideHeight }) => new ResizeObserver((entries) => {
            const entry = entries[0];
            // Call with null to let browser recalculate list height, otherwise it will have the same height as on first render, even after resize
            setSlideHeight(null);
            if (entry) {
                setSlideHeight(entry.target.getBoundingClientRect().height);
            }
        })),
    withHandlers(({ setSlideRef }) => ({
        setSlideRef: ({ resizeObserver, setSlideHeight }) => (ref) => {
            if (setSlideRef) {
                setSlideRef(ref);
            }

            if (ref != null) {
                setSlideHeight(null);
                resizeObserver.observe(ref);
            } else {
                resizeObserver.disconnect();
            }
        },
        handlerTransitionEnd: props => e =>
            e.target === e.currentTarget && props.expanded
    })),
    Wrapped => (props) => {
        const {
            setExpandAnimation,
            setCollapseAnimation,
            expanded
        } = props;

        useUpdateEffect(() => {
            if (expanded) {
                setExpandAnimation(true);

                setTimeout(() => setExpandAnimation(false), 0);
            } else {
                setCollapseAnimation(true);
                setTimeout(() => setCollapseAnimation(false), 330);
            }
        }, [expanded]);

        return <Wrapped {...props} />;
    },
    mapProps(omit([
        'resizeObserver',
        'setResizeObserver',
        'setExpandAnimation',
        'setCollapseAnimation',
        'setSlideHeight'
    ]))
)(GridPagingSlider);
